import * as React from 'react';
import { QueryClientConfig } from '../types';
import { DefaultQueryClientProvider } from './DefaultQueryClientProvider';
import {
  AppRouter,
  AuthProvider,
  ErrorBoundary,
  GlobalSettingsProvider,
} from '@mc/react-app-base';
import { AppErrorPage } from '../pages/AppErrorPage';
import { useCallback } from 'react';
import { GlobalAppError } from '@mc/react-app-base';
import { LoadDataApiError } from '@mc/axios-api';
import { AppRouterProps } from '@mc/react-app-base/dist/components/AppRouter';
import { Spinner } from '@mc/ui-v4';

type AppProps = React.PropsWithChildren<{
  queryClientConfig?: QueryClientConfig;
  /**
   * Prop které se předají do AppRouter komponenty.
   */
  appRouterProps?: AppRouterProps;
  /**
   * Prop které se předají do GlobalSettingsProvider komponenty, renderCustomLoading je již definován v app v4, ale je ho možné překrýt, pokud by bylo potřeba.
   */
  globalSettingsProps?: Omit<
    React.ComponentProps<typeof GlobalSettingsProvider>,
    'children'
  >;
}>;

/**
 * Řeší:
 * - error boundary
 * - app router
 * - auth provider
 * - query client provider pro react-query
 */
export const DefaultProviders: React.FC<AppProps> = ({
  children,
  queryClientConfig,
  globalSettingsProps,
  appRouterProps,
}) => {
  const renderCustomErrorErrorBoundary = useCallback(
    (error: GlobalAppError) => {
      let children;
      if (__DEV__) {
        children = (
          <>
            <pre>{error.infoStack}</pre>
            <pre>{error.infoError}</pre>
            <pre>{error.intoComponentError}</pre>
          </>
        );
      }

      return (
        <AppErrorPage message={error.message} description={error.description}>
          {children}
        </AppErrorPage>
      );
    },
    []
  );
  const renderCustomErrorAuthProvider = useCallback(
    (error: LoadDataApiError) => {
      return (
        <AppErrorPage message={error.message} description={error.description} />
      );
    },
    []
  );

  return (
    <ErrorBoundary renderCustomError={renderCustomErrorErrorBoundary}>
      <AppRouter {...appRouterProps}>
        <AuthProvider renderCustomError={renderCustomErrorAuthProvider}>
          <GlobalSettingsProvider
            renderCustomLoading={() => <Spinner />}
            {...globalSettingsProps}
          >
            <DefaultQueryClientProvider config={queryClientConfig}>
              {children}
            </DefaultQueryClientProvider>
          </GlobalSettingsProvider>
        </AuthProvider>
      </AppRouter>
    </ErrorBoundary>
  );
};
